/**
 * DOODIGITAL Page Speed Optimization Script
 * Version: 2.0
 * Author: Doo Digital
 * 
 * Features:
 * - Script deferral until user interaction
 * - Lazy loading for images and iframes
 * - DOM reorganization for optimal rendering
 * - Platform detection to avoid compatibility issues
 * 
 * Usage: Add defer="defer" to script tag in theme.liquid <head>
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION - Customize per project
  // ============================================
  
  var CONFIG = {
    // Number of images to load immediately (above-the-fold)
    eagerImageCount: 20,
    
    // Maximum time (ms) for DOM reorganization
    domReorgTimeout: 500,
    
    // Enable/disable features
    enableLazyImages: true,
    enableLazyIframes: true,
    enableDomReorg: true,
    
    // Debug mode (set to true to see console logs)
    debug: false
  };

  // ============================================
  // PLATFORM DETECTION
  // ============================================
  
  function shouldOptimize() {
    var ua = navigator.userAgent.toLowerCase();
    
    // Skip optimization on problematic platforms
    var excludedPlatforms = ['cros', 'crkey', 'ppc', 'matrix'];
    for (var i = 0; i < excludedPlatforms.length; i++) {
      if (ua.indexOf(excludedPlatforms[i]) !== -1) {
        if (CONFIG.debug) console.log('[PageSpeed] Skipping optimization for platform:', excludedPlatforms[i]);
        return false;
      }
    }
    
    // Only run on Chrome/Firefox
    if (!ua.match(/chrome|firefox/)) {
      if (CONFIG.debug) console.log('[PageSpeed] Skipping optimization - unsupported browser');
      return false;
    }
    
    // Skip if ?no-optimization is in URL
    if (window.location.href.indexOf('no-optimization') !== -1) {
      if (CONFIG.debug) console.log('[PageSpeed] Skipping optimization - disabled via URL param');
      return false;
    }
    
    return true;
  }

  // ============================================
  // LAZY LOADING FOR IMAGES
  // ============================================
  
  function applyLazyImages() {
    if (!CONFIG.enableLazyImages) return;
    
    var images = document.querySelectorAll('img:not([loading])');
    var imageCount = 0;
    
    images.forEach(function(img) {
      imageCount++;
      
      // Keep first N images eager for LCP
      if (imageCount <= CONFIG.eagerImageCount) {
        img.setAttribute('loading', 'eager');
        if (imageCount <= 3) {
          img.setAttribute('fetchpriority', 'high');
        }
      } else {
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      }
    });
    
    if (CONFIG.debug) console.log('[PageSpeed] Applied lazy loading to', imageCount, 'images');
  }

  // ============================================
  // LAZY LOADING FOR IFRAMES
  // ============================================
  
  function applyLazyIframes() {
    if (!CONFIG.enableLazyIframes) return;
    
    var iframes = document.querySelectorAll('iframe[src]');
    var iframeCount = 0;
    
    iframes.forEach(function(iframe) {
      // Skip already processed iframes
      if (iframe.hasAttribute('data-src')) return;
      
      var src = iframe.getAttribute('src');
      
      // Skip critical iframes (payment, essential embeds)
      if (src.indexOf('checkout') !== -1 || 
          src.indexOf('payment') !== -1 ||
          src.indexOf('recaptcha') !== -1) {
        return;
      }
      
      // Convert to lazy loading
      iframe.setAttribute('data-src', src);
      iframe.removeAttribute('src');
      iframe.setAttribute('loading', 'lazy');
      iframeCount++;
    });
    
    // Intersection Observer to load iframes when visible
    if ('IntersectionObserver' in window && iframeCount > 0) {
      var iframeObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var iframe = entry.target;
            if (iframe.hasAttribute('data-src')) {
              iframe.src = iframe.getAttribute('data-src');
              iframe.removeAttribute('data-src');
              iframeObserver.unobserve(iframe);
            }
          }
        });
      }, { rootMargin: '200px' });
      
      document.querySelectorAll('iframe[data-src]').forEach(function(iframe) {
        iframeObserver.observe(iframe);
      });
    }
    
    if (CONFIG.debug) console.log('[PageSpeed] Applied lazy loading to', iframeCount, 'iframes');
  }

  // ============================================
  // DOM REORGANIZATION
  // ============================================
  
  function reorganizeDOM() {
    if (!CONFIG.enableDomReorg) return;
    
    var startTime = Date.now();
    var head = document.head;
    var movedCount = 0;
    
    // Move misplaced meta tags to head
    var metaTags = document.body.querySelectorAll('meta');
    metaTags.forEach(function(meta) {
      if (Date.now() - startTime > CONFIG.domReorgTimeout) return;
      head.appendChild(meta);
      movedCount++;
    });
    
    // Move misplaced link tags to head
    var linkTags = document.body.querySelectorAll('link[rel="stylesheet"], link[rel="preload"]');
    linkTags.forEach(function(link) {
      if (Date.now() - startTime > CONFIG.domReorgTimeout) return;
      head.appendChild(link);
      movedCount++;
    });
    
    if (CONFIG.debug) console.log('[PageSpeed] Moved', movedCount, 'elements to head');
  }

  // ============================================
  // PRELOAD CRITICAL RESOURCES
  // ============================================
  
  function preloadCriticalResources() {
    // This function can be extended to dynamically preload resources
    // Currently handled via Liquid in theme.liquid
  }

  // ============================================
  // INITIALIZE
  // ============================================
  
  function init() {
    if (!shouldOptimize()) return;
    
    if (CONFIG.debug) console.log('[PageSpeed] Initializing optimization...');
    
    // Run immediately for critical optimizations
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        applyLazyImages();
        applyLazyIframes();
        reorganizeDOM();
      });
    } else {
      applyLazyImages();
      applyLazyIframes();
      reorganizeDOM();
    }
  }

  // Start optimization
  init();

})();
